/**
 * The drawing's pending agent proposals: its elements the agent wrote that no SME
 * has saved since. The asset rail flags their rows, and its Needs review filter
 * lists only them.
 *
 * Separate from the workspace so the pending rule has one home on this side.
 * It mirrors `is_pending_agent_proposal` in the backend's `models/pnid_schema.py`,
 * which the plant agent review queue applies to the same stored elements. The items
 * carry the facets `components/assetFilterTiers` filters by, read from
 * `targetRailItems` so the asset rail's rows and these items share one rule.
 */
import type { AssetFacets } from '../components/assetFilterTiers';
import type { PnidEquipment, PnidInstrument, PnidNode, PnidWizardState, MarkingKind } from '../types';
import { equipmentFacets, instrumentFacets, nodeFacets } from './targetRailItems';

export const AGENT_PROVENANCE_SOURCE = 'agent';

/** The kinds an agent review key can name. */
export const AGENT_REVIEW_KINDS: MarkingKind[] = ['instrument', 'node', 'equipment'];

export interface AgentReviewItem extends AssetFacets {
  /** `<kind>:<id>`, unique across the three collections. */
  key: string;
  id: string;
  label: string;
  /** The items sort by this: the subtype, else the type, else "Unspecified". */
  group: string;
}

const UNSPECIFIED_GROUP = 'Unspecified';

export function isPendingAgentProposal(
  provenanceSource: string | null | undefined,
  status: string | null | undefined,
): boolean {
  return provenanceSource === AGENT_PROVENANCE_SOURCE && status !== 'final_and_reviewed';
}

export function agentReviewKey(kind: MarkingKind, id: string): string {
  return `${kind}:${id}`;
}

export function parseAgentReviewKey(key: string): { kind: MarkingKind; id: string } | null {
  const separator = key.indexOf(':');
  if (separator <= 0) return null;
  const kind = key.slice(0, separator) as MarkingKind;
  if (!AGENT_REVIEW_KINDS.includes(kind)) return null;
  return { kind, id: key.slice(separator + 1) };
}

function pendingOf<T extends { id: string; status?: unknown; provenance?: { source: string } | null }>(
  elements: T[],
): T[] {
  return elements.filter((element) =>
    isPendingAgentProposal(element.provenance?.source, element.status as string | null | undefined),
  );
}

function instrumentItem(instrument: PnidInstrument): AgentReviewItem {
  const facets = instrumentFacets(instrument);
  return {
    key: agentReviewKey('instrument', instrument.id),
    kind: 'instrument',
    id: instrument.id,
    // The printed tag first, as the plant queue page labels it; the generated id
    // names only an element with no tag text, such as an OPC.
    label: instrument.instrument_text || instrument.generated_id || 'Untitled',
    ...facets,
    group: facets.subtype || facets.element_type || UNSPECIFIED_GROUP,
  };
}

function nodeItem(node: PnidNode): AgentReviewItem {
  const facets = nodeFacets(node);
  return {
    key: agentReviewKey('node', node.id),
    kind: 'node',
    id: node.id,
    label: node.line_no || `Node #${node.node_no}`,
    ...facets,
    group: facets.element_type || UNSPECIFIED_GROUP,
  };
}

function equipmentItem(equipment: PnidEquipment): AgentReviewItem {
  const facets = equipmentFacets(equipment);
  return {
    key: agentReviewKey('equipment', equipment.id),
    kind: 'equipment',
    id: equipment.id,
    label: equipment.equipment_no || 'Untitled',
    ...facets,
    group: facets.subtype || facets.element_type || UNSPECIFIED_GROUP,
  };
}

function byGroupThenLabel(a: AgentReviewItem, b: AgentReviewItem): number {
  return a.group.localeCompare(b.group) || a.label.localeCompare(b.label);
}

/** Every pending agent element on the drawing: instruments, then lines, then equipment. */
export function agentReviewItems(state: PnidWizardState | null): AgentReviewItem[] {
  if (!state) return [];
  return [
    ...pendingOf(state.instruments ?? []).map(instrumentItem).sort(byGroupThenLabel),
    ...pendingOf(state.nodes ?? []).map(nodeItem).sort(byGroupThenLabel),
    ...pendingOf(state.equipments ?? []).map(equipmentItem).sort(byGroupThenLabel),
  ];
}
